import * as React from "react";

import { cn } from "@/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  state?: "default" | "error" | "success";
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      hint,
      state = "default",
      value,
      defaultValue,
      onFocus,
      onBlur,
      onChange,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    React.useImperativeHandle(
      ref,
      () => innerRef.current as HTMLTextAreaElement,
    );

    const [currentValue, setCurrentValue] = React.useState<string>(
      (value ?? defaultValue ?? "").toString(),
    );
    const [focused, setFocused] = React.useState(false);

    React.useEffect(() => {
      if (value !== undefined) {
        setCurrentValue((value as any)?.toString() ?? "");
      }
    }, [value]);

    const hasText = currentValue.trim().length > 0;
    void (focused || hasText);

    return (
      <label className="relative block w-full">
        <div
          className={cn(
            "rounded-md p-[1px] transition-colors",
            "bg-transparent",
          )}
        >
          <div className="relative">
            {label && (
              <span
                className={cn(
                  "absolute left-3 text-garnet pointer-events-none transition-all duration-150 will-change-transform",
                  "top-0 text-[14px]",
                )}
              >
                {label}
              </span>
            )}

            <textarea
              ref={innerRef}
              value={value}
              defaultValue={defaultValue}
              rows={4}
              className={cn(
                "w-full min-h-[100px] resize-y rounded-md bg-fog text-garnet placeholder-transparent outline-none transition-colors",
                "pt-[28px] pb-[10px] px-3",
                state === "error" && "border border-red-500",
                state === "success" && "border border-green-600",
                state === "default" && "border border-transparent",
                className,
              )}
              {...props}
              onFocus={(e) => {
                setFocused(true);
                onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur?.(e);
              }}
              onChange={(e) => {
                setCurrentValue(e.currentTarget.value);
                onChange?.(e);
              }}
            />
          </div>
        </div>

        {hint && (
          <span
            className={cn(
              "mt-1 text-[11px]",
              state === "error" ? "text-red-500" : "text-garnet",
            )}
          >
            {hint}
          </span>
        )}
      </label>
    );
  },
);

Textarea.displayName = "Textarea";

export default Textarea;
